import type {ReactElement} from 'react';
import React from 'react';
import {BaseGlowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-glow-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {ShadowOrGlowType} from '@postermywall/fabricjs-2';
import {GlowType} from '@PosterWhiteboard/poster/poster-backend.types';
import type {ShadowOrGlowOptionsObject} from '@Utils/fabric-shadow.util';
import {getDefaultFabricShadowForType, getFabricShadowOrGlowTypeFromGlowType, isGlow} from '@Utils/fabric-shadow.util';
import {useGetShadowOptionsObjectOfActiveSlideItem} from '@Hooks/poster-editor/useGetShadowOptionsObjectOfActiveSlideItem';
import type {RGBA} from '@Utils/color.util';

interface SlideshowItemGlowOptionProps {
  onClickColorFill(): void;
}

export function SlideshowItemGlowOption({...props}: SlideshowItemGlowOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const activeSlideShadowOptions = useGetShadowOptionsObjectOfActiveSlideItem();

  if (!activeItem?.isSlideshow()) {
    throw new Error(`Expected slideshow item but got ${activeItem?.gitype}`);
  }
  if (!activeSlideUID) {
    throw new Error('unable to get the selected slide');
  }
  if (!activeSlide) {
    throw new Error(`unable to get the selected slide with uid ${activeSlideUID}`);
  }
  if (activeSlideShadowOptions === undefined) {
    throw new Error('selected slide item does not have shadow property');
  }

  const getSelectedGlowType = (activeSlideShadowOptions: ShadowOrGlowOptionsObject | null): GlowType => {
    if (!activeSlideShadowOptions) {
      return GlowType.NONE;
    }

    switch (activeSlideShadowOptions.shadowOrGlowType) {
      case ShadowOrGlowType.LIGHT_GLOW:
        return GlowType.LIGHT;
      case ShadowOrGlowType.STRONG_GLOW:
        return GlowType.STRONG;
      case null:
      case ShadowOrGlowType.CUSTOM_SHADOW:
      case ShadowOrGlowType.STRONG_SHADOW:
      case ShadowOrGlowType.LIGHT_SHADOW:
        return GlowType.NONE;
      default:
        throw new Error(`Unhandled shadowOrGlowType type: ${activeSlideShadowOptions.shadowOrGlowType} in SlideshowItemGlowOption`);
    }
  };

  const getGlowColor = (): RGBA => {
    if (!activeSlideShadowOptions) {
      return [0, 0, 0, 0];
    }

    return activeSlideShadowOptions.color;
  };

  const onClickHandler = (glowType: GlowType): void => {
    const newType = getFabricShadowOrGlowTypeFromGlowType(glowType);
    const currentType = activeSlideShadowOptions?.shadowOrGlowType;

    const updatedShadow = getDefaultFabricShadowForType(newType, activeSlide.gitype, activeSlide.getScaleForShadow());

    if (!updatedShadow) {
      void activeItem.updateSlideFromObject(activeSlideUID, {shadow: null});

      return;
    }

    const shouldPreserveColor = isGlow(currentType) && isGlow(newType);

    const {color, ...restShadow} = updatedShadow;

    const shadowToApply = shouldPreserveColor ? {...restShadow} : updatedShadow;

    void activeItem.updateSlideFromObject(activeSlideUID, {shadow: shadowToApply});
  };

  return (
    <BaseGlowOption
      glowColor={getGlowColor()}
      glowType={getSelectedGlowType(activeSlideShadowOptions)}
      onApplyGlow={onClickHandler}
      onClickColorFill={(): void => {
        props.onClickColorFill();
      }}
    />
  );
}
